import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const FeaturesCarousel: React.FC = () => {
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = React.useState(false);
    const [showRightArrow, setShowRightArrow] = React.useState(true);

    // Track scroll position to show/hide arrows
    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    React.useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            handleScroll(); // Initial check
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Hide scrollbar while keeping scroll functionality
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .features-carousel-scroll::-webkit-scrollbar {
                display: none;
            }
            .features-carousel-scroll {
                -ms-overflow-style: none;
                scrollbar-width: none;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const features = [
        {
            icon: '/career/ai_matching.svg',
            title: 'Smart Matching',
            description: 'AI analyzes resumes and job descriptions to deliver the best-fit candidates instantly'
        },
        {
            icon: '/career/opportunity.svg',
            title: 'Automated Outreach',
            description: 'Personalized messages sent on your behalf, saving time for both recruiters and candidates'
        },
        {
            icon: '/career/connect.svg',
            title: 'Direct Chat',
            description: 'When there\'s mutual interest, David opens a direct chat for seamless communication'
        },
        {
            icon: '/career/dashboard.svg',
            title: 'Employer Dashboard',
            description: 'Manage your entire pipeline — profiles, chats, interviews — in one place'
        },
        {
            icon: '/career/calendar.svg',
            title: 'Interview Scheduling',
            description: 'Integrated scheduling tools keep interviews and follow-ups running smoothly'
        }
    ];

    const handlePrevious = () => {
        if (scrollContainerRef.current) {
            const isMobile = window.innerWidth < 1024;
            const scrollAmount = isMobile ? 280 + 24 : 379 + 48; // card width + gap (mobile vs desktop)
            scrollContainerRef.current.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const handleNext = () => {
        if (scrollContainerRef.current) {
            const isMobile = window.innerWidth < 1024;
            const scrollAmount = isMobile ? 280 + 24 : 379 + 48; // card width + gap (mobile vs desktop)
            scrollContainerRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <section className="relative py-8 sm:py-10 lg:py-12">
            <div className="container mx-auto max-w-[1640px] px-4">
                {/* Section Title with Navigation Arrows */}
                <div className="flex items-center justify-between mb-6 sm:mb-8 lg:mb-10">
                    <h2
                        className="text-left flex-1"
                        style={{
                            fontFamily: 'Manrope',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            fontSize: 'clamp(32px, 6vw, 72px)',
                            lineHeight: 'clamp(40px, 7vw, 90px)',
                            letterSpacing: '-0.05em',
                            color: '#FFFFFF',
                        }}
                    >
                        A better way to discover opportunities
                    </h2>

                    {/* Navigation Arrows */}
                    <div className="flex gap-3 ml-4">
                        <button
                            onClick={handlePrevious}
                            disabled={!showLeftArrow}
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: showLeftArrow ? 'linear-gradient(87.95deg, #015FA6 -15.78%, #B7E6FF 102.06%)' : 'rgba(255, 255, 255, 0.1)',
                                border: 'none',
                                cursor: showLeftArrow ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                opacity: showLeftArrow ? 1 : 0.4,
                                transition: 'opacity 0.3s ease, background 0.3s ease',
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M15 18L9 12L15 6" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>

                        <button
                            onClick={handleNext}
                            disabled={!showRightArrow}
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: showRightArrow ? 'linear-gradient(87.95deg, #015FA6 -15.78%, #B7E6FF 102.06%)' : 'rgba(255, 255, 255, 0.1)',
                                border: 'none',
                                cursor: showRightArrow ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                opacity: showRightArrow ? 1 : 0.4,
                                transition: 'opacity 0.3s ease, background 0.3s ease',
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M9 18L15 12L9 6" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Carousel Container - Full Width */}
            <div
                ref={scrollContainerRef}
                className="relative features-carousel-scroll"
                style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {/* Cards Container */}
                <div
                    className="flex gap-6 lg:gap-12"
                    style={{
                        paddingLeft: 'max(1rem, calc((100vw - 1640px) / 2 + 1rem))',
                        paddingBottom: '8px', // Prevent scroll cut-off
                    }}
                >
                        {features.map((feature, index) => {
                            const cardContent = (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    whileHover={{ scale: index === 0 ? 1.02 : 1 }}
                                    className="min-w-[280px] lg:min-w-[379px] h-[300px] lg:h-[380px]"
                                    style={{
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: 'clamp(16px, 4vw, 24px)',
                                        gap: 'clamp(12px, 3vw, 24px)',
                                        background: 'radial-gradient(146.13% 118.42% at 50% -15.5%, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 99.59%), linear-gradient(180deg, rgba(46, 51, 90, 0) 0%, rgba(28, 27, 51, 0.1) 100%)',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                        boxShadow: 'inset 0px 0px 100px rgba(204, 215, 255, 0.15)',
                                        filter: 'drop-shadow(0px 5px 10px rgba(0, 0, 0, 0.05)) drop-shadow(0px 15px 30px rgba(0, 0, 0, 0.05)) drop-shadow(0px 30px 60px rgba(0, 0, 0, 0.1))',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: 'clamp(24px, 6vw, 40px)',
                                        cursor: index === 0 ? 'pointer' : 'default',
                                        marginRight: index === features.length - 1 ? 'max(1rem, calc((100vw - 1640px) / 2 + 1rem))' : '0',
                                    }}
                                >
                                {/* Icon */}
                                <img
                                    src={feature.icon}
                                    alt={feature.title}
                                    style={{
                                        width: 'clamp(50px, 12vw, 87px)',
                                        height: 'clamp(50px, 12vw, 87px)',
                                        objectFit: 'contain',
                                        flex: 'none',
                                        order: 0,
                                        flexGrow: 0
                                    }}
                                />

                                {/* Title */}
                                <h3
                                    style={{
                                        width: '100%',
                                        fontFamily: 'Manrope',
                                        fontStyle: 'normal',
                                        fontWeight: 700,
                                        fontSize: 'clamp(20px, 4vw, 32px)',
                                        lineHeight: 'clamp(28px, 6vw, 55px)',
                                        textAlign: 'center',
                                        letterSpacing: '-0.05em',
                                        color: '#FFFFFF',
                                        margin: 0,
                                        flex: 'none',
                                        order: 1,
                                        flexGrow: 0
                                    }}
                                >
                                    {feature.title}
                                </h3>

                                {/* Description */}
                                <p
                                    style={{
                                        width: '100%',
                                        fontFamily: 'Manrope',
                                        fontStyle: 'normal',
                                        fontWeight: 300,
                                        fontSize: 'clamp(14px, 3vw, 24px)',
                                        lineHeight: 'clamp(20px, 4vw, 33px)',
                                        textAlign: 'center',
                                        letterSpacing: '-0.05em',
                                        color: '#FFFFFF',
                                        margin: 0,
                                        flex: 'none',
                                        order: 2,
                                        flexGrow: 0
                                    }}
                                >
                                    {feature.description}
                                </p>
                            </motion.div>
                            );

                            return index === 0 ? (
                                <Link key={index} to="/blog" style={{ textDecoration: 'none' }}>
                                    {cardContent}
                                </Link>
                            ) : cardContent;
                        })}
                    </div>
            </div>
        </section>
    );
};

export default FeaturesCarousel;
